/**
 * ExternalDialogNode - Node component for external dialog references
 * 
 * Represents a dialog that is referenced from the current dialog but not part of it.
 * Used to visualize cross-dialog connections in flow mode.
 */

import React, { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import IconProvider from '@/components/ui/IconProvider';
import CardBase from '@/components/cards/CardBase';
import { useNavigate } from 'react-router-dom';

interface ExternalDialogNodeData {
  dialogId: string;
  dialogName: string;
  dialogKey?: string;
  targetSection?: string;
  sectionName?: string;
  [key: string]: any;
}

export const ExternalDialogNode = memo(({ data, selected }: NodeProps<any>) => {
  const navigate = useNavigate();
  const nodeData = data as ExternalDialogNodeData;
  
  const handleOpenDialog = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (nodeData.dialogId) {
      navigate(`/dialogs/${nodeData.dialogId}`);
    }
  };
  
  return (
    <CardBase
      shadowType="black"
      shadowColor="#000000"
      backgroundColor="#ffffff"
      border={true}
      borderColor={selected ? "#3b82f6" : "#000000"}
      className="min-w-[280px] max-w-[400px]"
      style={{
        borderStyle: 'dashed',
        borderWidth: '2px',
      }}
    >
      {/* Connection Handle - only target (receives connections) */}
      <Handle 
        type="target" 
        position={Position.Left}
        id="left"
        className="w-3 h-3 !bg-orange-500"
      />
      
      {/* Header - External Dialog indicator */}
      <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border-b border-orange-300">
        <IconProvider iconstring="fa-arrow-up-right-from-square" size={14} className="text-orange-700" />
        <span className="flex-1 font-semibold text-xs uppercase text-orange-700">
          External Dialog
        </span>
        <button
          onClick={handleOpenDialog}
          className="hover:bg-orange-100 p-1 rounded transition-colors"
          title="Open in new tab"
        >
          <IconProvider 
            iconstring="fa-external-link" 
            size={12} 
            className="text-orange-700"
          />
        </button>
      </div>

      {/* Dialog Info */}
      <div className="px-3 py-2">
        <div className="space-y-2">
          {/* Dialog Name */}
          <div>
            <div className="text-sm font-semibold text-gray-800">
              {nodeData.dialogName}
            </div>
            {nodeData.dialogKey && (
              <div className="text-xs font-mono text-muted-foreground">
                {nodeData.dialogKey}
              </div>
            )}
          </div>
          
          {/* Target Section */}
          {nodeData.targetSection && (
            <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 rounded text-xs">
              <IconProvider iconstring="fa-bullseye" size={10} className="text-blue-600" />
              <span className="text-blue-700">
                → {nodeData.sectionName || nodeData.targetSection}
              </span>
            </div>
          )}
          
          {/* Info badge */}
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <IconProvider iconstring="fa-info-circle" size={10} />
            <span>Click to open this dialog</span>
          </div>
        </div>
      </div>
    </CardBase>
  );
});

ExternalDialogNode.displayName = 'ExternalDialogNode';

export default ExternalDialogNode;

